<form [formGroup]="accountForm" class="space-y-6">
    <h2 class="text-2xl font-semibold">Account</h2>

    <!-- EMAIL -->
    <div>
        <label class="block text-sm font-medium mb-1">Email address</label>

        <div class="flex w-full relative">
            <input type="email" formControlName="email" [disabled]="emailVerified" placeholder="Enter your email"
                class="flex-1 border rounded-l-md px-3 py-2 focus:ring focus:ring-blue-300" />

            <!-- Button (Validate / Resend / Verified) -->
            <ng-container *ngIf="!emailVerified; else verifiedBadge">
                <button
                type="button"
                (click)="sessionId ? onResendEmail() : onValidateEmail()"
                [disabled]="
                  !accountForm.get('email')?.value ||
                  accountForm.get('email')?.invalid
                "
                class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-r-md 
                       hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-blue-400">
                {{ sessionId ? (timer > 0 ? timerDisplay : 'Resend Email') : 'Validate Email' }}
              </button>
            </ng-container>

            <!-- ✅ Verified badge -->
            <ng-template #verifiedBadge>
                <div class="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-r-md flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    Verified
                </div>
            </ng-template>
        </div>

        <!-- Errors -->
        <div *ngIf="accountForm.get('email')?.touched && accountForm.get('email')?.invalid"
            class="text-sm text-red-600 mt-1">
            <div *ngIf="accountForm.get('email')?.errors?.['required']">Email is required.</div>
            <div *ngIf="accountForm.get('email')?.errors?.['invalidEmail']">
              {{ textService.getText('form_validate_email') }}
            </div>
        </div>
    </div>

    <!-- PASSCODE (only if email validated) -->
    <div *ngIf="!emailVerified && sessionId">
        <label class="block text-sm font-medium mb-1">Passcode</label>
        <div class="flex w-full">
            <input type="text" formControlName="Passcode" [disabled]="!sessionId || emailVerified"
                placeholder="Enter your Passcode"
                class="flex-1 border rounded-l-md px-3 py-2 focus:ring focus:ring-blue-300" />
            <button type="button" (click)="onValidatePasscode()" [disabled]="!sessionId || emailVerified" class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-r-md 
               hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-blue-400">
                Validate Passcode
            </button>
        </div>

        <span class="text-xs text-gray-500">
            Please enter your verification code. You have 3 attempts until the time is up.
        </span>

        <div *ngIf="accountForm.get('Passcode')?.touched && accountForm.get('Passcode')?.invalid"
            class="text-sm text-red-600 mt-1">
            Passcode is required.
        </div>

        <div class="mt-1">
            <p *ngIf="passcodeStatus === 'VERIFIED'" class="text-green-600 text-sm font-medium">
                ✅ Code verified successfully.
            </p>
            <p *ngIf="passcodeStatus === 'INVALID'" class="text-red-600 text-sm font-medium">
                Invalid code, please try again.
            </p>
            <p *ngIf="passcodeStatus === 'INVALID' && !sessionId" class="text-red-600 text-sm font-medium mt-1">
                Your session expired. Please validate your email again to receive a new code.
            </p>
        </div>
    </div>

    <!-- USERNAME -->
    <div>
        <label class="block text-sm font-medium mb-1">
            {{ textService.getText('form_label_cloud_username') }}
        </label>
        <div class="relative">
            <input type="text" formControlName="username"
                class="w-full border rounded-md px-3 py-2 pr-32 focus:ring focus:ring-blue-300" />

            <!-- ✅ Success -->
            <div *ngIf="accountForm.get('username')?.valid && (accountForm.get('username')?.dirty || accountForm.get('username')?.touched)"
                class="absolute inset-y-0 right-3 flex items-center text-green-600 text-sm pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round"
                        d="M9 12l2 2l4-4m5 2a9 9 0 11-18 0a9 9 0 0118 0z" />
                </svg>
                Username is available
            </div>
        </div>

        <!-- ❌ Errors -->
        <div *ngIf="accountForm.get('username')?.invalid && (accountForm.get('username')?.dirty || accountForm.get('username')?.touched)"
            class="text-sm text-red-600 mt-1">
            <div *ngIf="accountForm.get('username')?.errors?.['required']">
                {{ textService.getText('form_validate_cloud_username_empty') }}
            </div>
            <div *ngIf="accountForm.get('username')?.errors?.['minlength']">
                Username must be at least 6 characters.
            </div>
            <div *ngIf="accountForm.get('username')?.errors?.['maxlength']">
                Maximum username length is 16 characters.
            </div>
            <div *ngIf="accountForm.get('username')?.errors?.['usernameTaken']">
                This username is already taken.
            </div>
            <div *ngIf="accountForm.get('username')?.errors?.['usernameCheckFailed']">
                Could not validate username. Please try again later.
            </div>
        </div>
    </div>

    <!-- PASSWORD -->
    <div class="relative">
        <label class="block text-sm font-medium mb-1">
            {{ textService.getText('form_label_cloud_password') }}
        </label>
        <input [type]="showPassword ? 'text' : 'password'" formControlName="password"
            class="w-full border rounded-md px-3 py-2 pr-10 focus:ring focus:ring-blue-300" />
        <button type="button" (click)="showPassword = !showPassword"
            class="absolute right-2 top-8 text-gray-500 hover:text-gray-700">
            <svg *ngIf="!showPassword" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none"
                viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <svg *ngIf="showPassword" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.969 
               9.969 0 012.88-4.419M9.88 9.88a3 3 0 104.24 4.24M6.1 6.1l11.8 11.8" />
            </svg>
        </button>

        <div *ngIf="accountForm.get('password')?.touched && accountForm.get('password')?.errors"
            class="text-sm text-red-600 mt-1">
            <div *ngIf="accountForm.get('password')?.errors?.['required']">
                Password is required.
            </div>
            <div *ngIf="accountForm.get('password')?.errors?.['strongPassword']">
                Use 10–16 characters, with at least one uppercase, one lowercase, and one number.
            </div>
        </div>
    </div>

    <!-- CONFIRM PASSWORD -->
    <div class="relative">
        <label class="block text-sm font-medium mb-1">
            {{ textService.getText('form_label_cloud_password_confirmation') }}
        </label>
        <input [type]="showConfirm ? 'text' : 'password'" formControlName="confirmPassword"
            class="w-full border rounded-md px-3 py-2 pr-10 focus:ring focus:ring-blue-300" />
        <button type="button" (click)="showConfirm = !showConfirm"
            class="absolute right-2 top-8 text-gray-500 hover:text-gray-700">
            <svg *ngIf="!showConfirm" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <svg *ngIf="showConfirm" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24"
                stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.969 
               9.969 0 012.88-4.419M9.88 9.88a3 3 0 104.24 4.24M6.1 6.1l11.8 11.8" />
            </svg>
        </button>

        <div *ngIf="accountForm.errors?.['passwordMismatch'] && accountForm.get('confirmPassword')?.touched"
            class="text-sm text-red-600 mt-1">
            Passwords do not match.
        </div>
    </div>
</form>